import { ID } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getEntityByIDResolver } from '@adornis/baseql/server/resolver/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Names } from '@ils/static/names.js';
import { DateTime } from 'luxon';
import { Slot } from '../db/slot.js';
import {
  checkForAnswersInSlotsResolver,
  createFallbackSlotAtNewTimeResolver,
  getExamSlotsForUserResolver,
  reportProblemForSlotResolver,
  subscribeSlotsForUserResolver,
  updateOperatorNotesForSlotResolver,
} from '../server/resolver/slot.js';

export const getExamSlotsForUser = registerQuery({
  operationName: 'getExamSlotsForUser',
  resolve: getExamSlotsForUserResolver,
  type: () => [Slot],
  params: [
    { name: 'examDayID', type: () => String },
    { name: 'userID', type: () => String },
  ],
});

export const subscribeSlotsForUser = registerSubscription({
  operationName: 'subscribeSlotsForUser',
  resolve: subscribeSlotsForUserResolver,
  type: () => [Slot],
  params: [
    { name: 'examDayID', type: () => String },
    { name: 'userID', type: () => String },
  ],
});

export const checkForAnswersInSlots = registerQuery({
  operationName: 'checkForAnswersInSlots',
  resolve: checkForAnswersInSlotsResolver,
  type: () => Boolean,
  params: [{ name: 'slotIDs', type: () => [String] }],
});

export const reportProblemForSlot = registerMutation({
  operationName: 'reportProblemForSlot',
  resolve: reportProblemForSlotResolver,
  type: () => Boolean,
  params: [{ name: 'slotID', type: () => ID }],
});

export const createFallbackSlotAtNewTime = registerMutation({
  operationName: 'createFallbackSlotAtNewTime',
  resolve: createFallbackSlotAtNewTimeResolver,
  type: () => Slot,
  params: [
    { name: 'examDayID', type: () => String },
    { name: 'failedSlotID', type: () => String },
    { name: 'newStartTime', type: () => DateTime },
    { name: 'newEndTime', type: () => DateTime },
  ],
});

const getSlotByIDWithoutCacheResolver =
  (slotID: string) =>
  async <const T extends BaseQLSelectionSet<Slot>>(gqlFields: T) => {
    const result = await getEntityByIDResolver<Slot>(Names.Slot._class, [slotID])(gqlFields);
    return result[0];
  };

export const getSlotByIDWithoutCache = registerQuery({
  operationName: 'getSlotByIDWithoutCache',
  resolve: getSlotByIDWithoutCacheResolver,
  type: () => Slot,
  params: [{ name: 'id', type: () => ID }],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
});

export const updateOperatorNotesForSlot = registerMutation({
  operationName: 'updateOperatorNotesForSlot',
  resolve: updateOperatorNotesForSlotResolver,
  type: () => Boolean,
  params: [
    { name: 'slotID', type: () => ID },
    { name: 'notes', type: () => String },
  ],
});
